/* eslint react/prop-types: 0 */
import _ from 'lodash';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { makeStyles } from '@material-ui/core/styles';

import useFetch from '../../services/UseFetch';
import Properties, { ROW_TYPES } from '../properties/Properties';
import { PageLink } from '../links/Links';
import { accessPointProperty, radioProperty } from '../properties/components';
import { dateTimeFormatter, DEFAULT_INVALID_VALUE } from '../../utilities/FormattingUtilities';
import { agentWirelessNetworkProperty } from './properties/AgentWirelessNetwork';
import { useAgent } from '../filters';
import IPAddressUtilities from '../../utilities/IPAddressUtilities';

const useStyles = makeStyles({
    wrapper: {
        display: 'flex',
        flexDirection: 'row',
    },
});

function isBlank(value) {
    return _.isNil(value) || _.trim(value) === '';
}

const strFn = (title, selector, validator) => ({
    title,
    hasValue: (data) => !isBlank(_.get(data, selector)),
    type: ROW_TYPES.FUNCTION,
    mapper: (data) => {
        let value = _.get(data, selector);
        if (_.isFunction(validator)) {
            value = validator(value);
        }
        if (isBlank(value)) return DEFAULT_INVALID_VALUE;
        if (value === 'Edge Sensor') {
            const edgeSensor = _.get(data, 'edgeSensorModel', '');
            const isHardware = _.get(data, 'isHardware', false);
            return isHardware ? `${value} ${edgeSensor}` : `${value} 290`;
        }
        return value;
    },
});

const dateTimeFn = (title, selector) => ({
    title,
    hasValue: (data) => !isBlank(_.get(data, selector)),
    type: ROW_TYPES.FUNCTION,
    mapper: (data) => {
        const value = _.get(data, selector);
        return dateTimeFormatter(value);
    },
});

const parseAgentSoftwareVersion = (versionString) => {
    const fullVersionParts = versionString.split('_');
    let softwareVersionParts = _.first(fullVersionParts).split('-');
    if (softwareVersionParts.length > 1) {
        softwareVersionParts = softwareVersionParts.slice(0, softwareVersionParts.length - 1);
    }
    return softwareVersionParts.join('-');
};

const parseAgentOS = (version) => {
    if (version.osName === 'macOS') {
        return `${version.osName}, ${version.osVersion}`;
    }
    return version.osName;
};

const baseProperties = [
    strFn('Status', 'status'),
    strFn('Name', 'name'),
    strFn('Model', 'modelLong'),
    strFn('Version', 'version.softwareVersion', parseAgentSoftwareVersion),
    strFn('Hostname', 'network.hostname'),
];
const ipv4Properties = [strFn('Internal IPv4', 'network.ipv4Local')];

const ipv6Properties = [strFn('Internal IPv6', 'network.ipv6Local', (it) => IPAddressUtilities.formatIPv6(it))];

const ipProperties = [strFn('External IP', 'network.ipExternal')];

const extraProperties = [
    strFn('Wired Admin MAC', 'network.mac'),
    strFn('OS', 'version', parseAgentOS),
    dateTimeFn('First Seen', 'createdAt'),
    dateTimeFn('Last Seen', 'lastSeenAt'),
    strFn('Serial Number', 'vitals.serial_number'),
    strFn('nPoint Location', 'location'),
    {
        title: i18n._(t`Smart Edge Monitoring`),
        type: ROW_TYPES.FUNCTION,
        mapper: (data) => {
            const { beaconPackets } = data || {};
            if (_.isUndefined(beaconPackets)) return DEFAULT_INVALID_VALUE;
            return beaconPackets ? t`Enabled` : t`Disabled`;
        },
    },
    {
        title: 'Sites',
        selector: 'sites',
        type: ROW_TYPES.FUNCTION,
        mapper: (data) => {
            const { sites } = data || {};

            if (!sites?.length) return DEFAULT_INVALID_VALUE;

            return (
                <div>
                    {sites.map(({ _id, name }) => (
                        <div key={_id}>
                            <PageLink target="/site-detail" targetName={name} filters={[{ name: 'Site', uuid: _id }]} />
                        </div>
                    ))}
                </div>
            );
        },
    },
];

const geoISPFields = [
    'geoISP.provider',
    'geoISP.isoCode',
    'geoISP.subdivisionIsoCode',
    'geoISP.city',
    'geoISP.subdivision',
    'geoISP.country',
    'geoISP.postal',
    'geoISP.latitude',
    'geoISP.longitude',
    'geoISP.network',
];

const GeoISPPropertiesDef = {
    properties: [
        strFn('ISP', 'geoISP.provider'),
        strFn('Country Code', 'geoISP.isoCode'),
        strFn('Subdivision Code', 'geoISP.subdivisionIsoCode'),
        strFn('City', 'geoISP.city'),
        strFn('Subdivision', 'geoISP.subdivision'),
        strFn('Country', 'geoISP.country'),
        strFn('Postal Code', 'geoISP.postal'),
        strFn('Latitude', 'geoISP.latitude'),
        strFn('Longitude', 'geoISP.longitude'),
        strFn('Network', 'geoISP.network'),
        {
            title: 'Location',
            hasValue: (data) => !_.isNil(data?.geoISP),
            type: ROW_TYPES.MAP,
            selector: 'geoISP',
        },
    ],
};

const wifiFields = [
    'wifiSsid',
    'wifiTechnology',
    'deviceId',
    'radioId',
    'wifiBssid',
    'vitals.wifi_mac',
    'wifiChannel',
    'wifiErrors',
];

const wifiPropertiesDef = [
    agentWirelessNetworkProperty,
    strFn('Wi-Fi Technology', 'wifiTechnology'),
    accessPointProperty('deviceName', 'device'),
    radioProperty('radioName', 'radio'),
    strFn('Wi-Fi BSSID', 'wifiBssid', false),
    strFn('Wi-Fi IP', 'currentIpAddress', false),
    strFn('Wi-Fi MAC', 'vitals.wifi_mac', false),
    strFn('Wi-Fi Channel', 'wifiChannel', false),
    {
        title: t`Wi-Fi Channel Frequency`,
        hasValue: (data) => !isBlank(_.get(data, 'wifiFrequency')),
        type: ROW_TYPES.FUNCTION,
        mapper: (data) => `${_.get(data, 'wifiFrequency')}GHz`,
    },
    strFn('Wi-Fi Errors', 'wifiErrors', false),
];

const switchPortFields = [
    'nearestSwitchProto',
    'nearestSwitch',
    'nearestSwitchIP',
    'nearestSwitchPort',
    'vitals.portVlan',
];

const switchPortPropertiesDef = [
    strFn('Wired Switch xDP Type', 'nearestSwitchProto'),
    strFn('Wired Switch Name', 'nearestSwitch'),
    strFn('Wired Switch IP', 'nearestSwitchIP'),
    strFn('Wired Switch Port', 'nearestSwitchPort'),
    strFn('Wired Switch VLAN', 'vitals.portVlan'),
];

const hasFields = (data, fields) =>
    fields.map((field) => _.get(data, field)).filter((field) => field !== undefined).length > 0;

export function AgentPropertiesPanel({ data }) {
    const classes = useStyles();

    return (
        <div className={classes.wrapper}>
            <Properties propertiesDef={getPropertiesDef()} data={data} />
            {hasFields(data, geoISPFields) && <Properties propertiesDef={GeoISPPropertiesDef} data={data} />}
            {_.get(getLastColumnPropertiesDef(data), 'properties', []) && (
                <Properties propertiesDef={getLastColumnPropertiesDef(data)} data={data} />
            )}
        </div>
    );
}

export default function AgentProperties() {
    const agent = useAgent();
    const properties = useFetch(`/query/admin/agents/${agent?.uuid}`);
    if (!properties) return null;
    return <AgentPropertiesPanel data={properties} />;
}

function getPropertiesDef() {
    return {
        properties: [...baseProperties, ...ipProperties, ...ipv4Properties, ...ipv6Properties, ...extraProperties],
    };
}

function getLastColumnPropertiesDef(data) {
    let properties = [];

    if (hasFields(data, wifiFields)) {
        properties = wifiPropertiesDef;
    }

    if (hasFields(data, switchPortFields)) {
        properties = [...properties, ...switchPortPropertiesDef];
    }

    return { properties };
}
